import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import DashboardLayout from '../layouts/DashboardLayout';
import {
  createSubject,
  deleteSubject,
  listSubjects,
  seedCapsPresets,
  updateSubject,
} from '../api/subjects';
import { useSnackbar } from 'notistack';

const phaseOptions = [
  { value: 'foundation', label: 'Foundation (G1-3)' },
  { value: 'intermediate', label: 'Intermediate (G4-6)' },
  { value: 'senior', label: 'Senior (G7-9)' },
];

const trackOptions = [
  { value: 'core', label: 'Core' },
  { value: 'home-language', label: 'Home Language' },
  { value: 'first-additional-language', label: 'First Additional Language' },
  { value: 'elective', label: 'Elective' },
];

const defaultLanguages = {
  hl: ['English HL', 'Afrikaans HL'],
  fal: ['Afrikaans FAL', 'IsiZulu FAL'],
};

const phaseFromGrade = (grade) => {
  if (!grade) return '';
  if (grade <= 3) return 'foundation';
  if (grade <= 6) return 'intermediate';
  if (grade <= 9) return 'senior';
  return '';
};

const AdminSubjectsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    grade: '',
    phase: '',
    track: '',
    isActive: 'all',
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [seedYear, setSeedYear] = useState(new Date().getFullYear());
  const [form, setForm] = useState({
    name: '',
    code: '',
    grade: '',
    phase: '',
    track: 'core',
    languageOptions: [],
    isMandatory: true,
    year: '',
    isActive: true,
  });
  const { enqueueSnackbar } = useSnackbar();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (filters.grade) params.grade = filters.grade;
      if (filters.track) params.track = filters.track;
      if (filters.isActive !== 'all') params.isActive = filters.isActive === 'active';
      const data = await listSubjects(params);
      const list = Array.isArray(data) ? data : [];
      const filtered = filters.phase
        ? list.filter((s) => s.phase === filters.phase)
        : list;
      setRows(filtered);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load subjects');
    } finally {
      setLoading(false);
    }
  }, [filters.grade, filters.isActive, filters.phase, filters.track]);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = useCallback((row) => {
    if (row) {
      setForm({
        name: row.name || '',
        code: row.code || '',
        grade: row.grade || '',
        phase: row.phase || '',
        track: row.track || 'core',
        languageOptions: row.languageOptions || [],
        isMandatory: row.isMandatory !== false,
        year: row.year || '',
        isActive: row.isActive !== false,
      });
    } else {
      setForm({
        name: '',
        code: '',
        grade: '',
        phase: '',
        track: 'core',
        languageOptions: [],
        isMandatory: true,
        year: '',
        isActive: true,
      });
    }
  }, []);

  const handleOpenCreate = () => {
    setSelectedRow(null);
    resetForm(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (row) => {
    setSelectedRow(row);
    resetForm(row);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setDialogLoading(true);
      const payload = {
        name: form.name,
        code: form.code || null,
        grade: Number(form.grade),
        phase: form.phase || phaseFromGrade(Number(form.grade)) || null,
        track: form.track,
        languageOptions: form.languageOptions || [],
        isMandatory: Boolean(form.isMandatory),
        year: form.year ? Number(form.year) : null,
        isActive: Boolean(form.isActive),
      };
      if (selectedRow?._id) {
        await updateSubject(selectedRow._id, payload);
        enqueueSnackbar('Subject updated', { variant: 'success' });
      } else {
        await createSubject(payload);
        enqueueSnackbar('Subject created', { variant: 'success' });
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to save subject', { variant: 'error' });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDelete = async (row) => {
    const confirmed = window.confirm(`Delete subject "${row.name}" (G${row.grade})?`);
    if (!confirmed) return;
    try {
      await deleteSubject(row._id);
      enqueueSnackbar('Subject deleted', { variant: 'success' });
      await load();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to delete subject', { variant: 'error' });
    }
  };

  const handleSeedCaps = async () => {
    try {
      await seedCapsPresets({ year: seedYear });
      enqueueSnackbar('CAPS subjects seeded', { variant: 'success' });
      await load();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to seed CAPS presets', { variant: 'error' });
    }
  };

  const columns = useMemo(() => ([
    { field: 'name', headerName: 'Subject', flex: 1, minWidth: 200 },
    { field: 'code', headerName: 'Code', minWidth: 120, valueGetter: (params) => params.row.code || '-' },
    { field: 'grade', headerName: 'Grade', minWidth: 90 },
    { field: 'phase', headerName: 'Phase', minWidth: 140 },
    { field: 'track', headerName: 'Track', minWidth: 180 },
    {
      field: 'languageOptions',
      headerName: 'Language Options',
      minWidth: 220,
      valueGetter: (params) => (params.row.languageOptions || []).join(', ') || '-',
    },
    {
      field: 'isMandatory',
      headerName: 'Mandatory',
      minWidth: 120,
      renderCell: (params) => (
        <Chip size="small" label={params.value ? 'Yes' : 'No'} color={params.value ? 'success' : 'default'} />
      ),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      minWidth: 120,
      renderCell: (params) => (
        <Chip size="small" label={params.value ? 'Active' : 'Inactive'} color={params.value ? 'success' : 'default'} />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Edit">
            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDelete(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ]), [handleDelete, handleOpenEdit]);

  return (
    <DashboardLayout
      title="Subjects (CAPS)"
      actions={(
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={load}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button startIcon={<AddIcon />} variant="contained" onClick={handleOpenCreate}>
            Add Subject
          </Button>
        </Stack>
      )}
    >
      <Box>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}

          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardHeader
              title="Seed CAPS subjects"
              subheader="Create the CAPS subjects for Grades 1-9 using presets."
              action={(
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    label="Year"
                    type="number"
                    size="small"
                    value={seedYear}
                    onChange={(e) => setSeedYear(e.target.value)}
                    sx={{ width: 120 }}
                  />
                  <Button startIcon={<PlaylistAddCheckIcon />} variant="outlined" onClick={handleSeedCaps}>
                    Seed CAPS
                  </Button>
                </Stack>
              )}
            />
          </Card>

          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardHeader
              title="Subjects"
              action={(
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    label="Grade"
                    type="number"
                    value={filters.grade}
                    onChange={(e) => setFilters((prev) => ({ ...prev, grade: e.target.value }))}
                    sx={{ minWidth: 110 }}
                  />
                  <TextField
                    select
                    size="small"
                    label="Phase"
                    value={filters.phase}
                    onChange={(e) => setFilters((prev) => ({ ...prev, phase: e.target.value }))}
                    sx={{ minWidth: 160 }}
                  >
                    <MenuItem value="">All phases</MenuItem>
                    {phaseOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Track"
                    value={filters.track}
                    onChange={(e) => setFilters((prev) => ({ ...prev, track: e.target.value }))}
                    sx={{ minWidth: 180 }}
                  >
                    <MenuItem value="">All tracks</MenuItem>
                    {trackOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Status"
                    value={filters.isActive}
                    onChange={(e) => setFilters((prev) => ({ ...prev, isActive: e.target.value }))}
                    sx={{ minWidth: 120 }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </TextField>
                </Stack>
              )}
            />
            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
              <Box sx={{ height: 520 }}>
                <DataGrid
                  rows={rows}
                  getRowId={(row) => row._id}
                  columns={columns}
                  loading={loading}
                  disableRowSelectionOnClick
                  sx={{
                    border: 'none',
                    color: 'text.primary',
                    bgcolor: 'background.paper',
                    '& .MuiDataGrid-virtualScroller': { bgcolor: 'background.paper' },
                    '& .MuiDataGrid-row': { bgcolor: 'background.paper' },
                    '& .MuiDataGrid-cell': { color: 'text.primary' },
                    '& .MuiDataGrid-cellContent': { color: 'text.primary' },
                    '& .MuiDataGrid-columnHeaders': { bgcolor: 'grey.50', color: 'text.primary' },
                    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, color: 'text.primary' },
                    '& .MuiDataGrid-footerContainer': { bgcolor: 'grey.50', color: 'text.primary' },
                    '& .MuiDataGrid-overlay': { color: 'text.secondary' },
                  }}
                  initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  pageSizeOptions={[10, 25, 50]}
                />
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
 <DialogTitle>{selectedRow ? 'Edit Subject' : 'Add Subject'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Code"
              value={form.code}
              onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))}
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Grade"
                type="number"
                value={form.grade}
                onChange={(e) => {
                  const grade = e.target.value;
                  setForm((prev) => ({ ...prev, grade, phase: prev.phase || phaseFromGrade(Number(grade)) }));
                }}
                fullWidth
              />
              <TextField
                select
                label="Phase"
                value={form.phase}
                onChange={(e) => setForm((prev) => ({ ...prev, phase: e.target.value }))}
                fullWidth
              >
                <MenuItem value="">Auto</MenuItem>
                {phaseOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField
              select
              label="Track"
              value={form.track}
              onChange={(e) => {
                const track = e.target.value;
                const languageOptions =
                  track === 'home-language'
                    ? defaultLanguages.hl
                    : track === 'first-additional-language'
                      ? defaultLanguages.fal
                      : [];
                setForm((prev) => ({
                  ...prev,
                  track,
                  languageOptions: prev.languageOptions.length ? prev.languageOptions : languageOptions,
                }));
              }}
              fullWidth
            >
              {trackOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
            {(form.track === 'home-language' || form.track === 'first-additional-language') && (
              <TextField
                select
                label="Language options"
                value={form.languageOptions}
                onChange={(e) => setForm((prev) => ({ ...prev, languageOptions: e.target.value }))}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => (selected.length ? selected.join(', ') : 'Select languages'),
                }}
                fullWidth
              >
                {(form.track === 'home-language' ? defaultLanguages.hl : defaultLanguages.fal).map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Year (optional)"
                type="number"
                value={form.year}
                onChange={(e) => setForm((prev) => ({ ...prev, year: e.target.value }))}
                fullWidth
              />
              <FormControlLabel
                control={(
                  <Switch
                    checked={form.isMandatory}
                    onChange={(e) => setForm((prev) => ({ ...prev, isMandatory: e.target.checked }))}
                  />
                )}
                label="Mandatory"
              />
              <FormControlLabel
                control={(
                  <Switch
                    checked={form.isActive}
                    onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                  />
                )}
                label="Active"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={dialogLoading}>
            {dialogLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default AdminSubjectsPage;
